import { useCallback, useContext, useMemo } from 'react';
import { APP_DOMAIN } from 'appenv';
import {
  addMetadata,
  ConversationsClient,
  ConversationType,
  selectTwilioClient,
  setChatInterface,
  setSelectedChat,
  setChatAlert,
  isTwilioConnected,
  ConversationMetadata,
  removeMetadata,
  setCollapseState,
  Conversation,
  haveAllConversationsLoaded,
  GroupChatType,
} from 'models/twilio';
import { batch, useDispatch, useSelector } from 'react-redux';
import { joinConversationRequest } from 'network/twilioRequests';
import useCurrentAttendee from './useCurrentAttendee';
import buildInitialMetadata from '../utils/buildInitialMetadata';
import useFirebaseChatConfig from './useFirebaseChatConfig';
import useMutableRef from './useMutableRef';
import ConverastionContext from '../ConversationContext';
import TRANSLATIONS from '../translations';

interface LocalizableErrorProps {
  message: string;
  transKey: string;
}
class LocalizableError extends Error {
  constructor(constructParams: LocalizableErrorProps) {
    const { message, transKey } = constructParams;
    super(message);
    this.message = message;
    this.name = transKey;
  }
}

interface JoinGroupChatConfig {
  groupChatType?: GroupChatType;
  displayInterface?: boolean;
}

const useTwilioEventFunctions = () => {
  const { uuid, exhibitorBoothId, isExhibitor } = useCurrentAttendee();
  const { exhibitorInteraction } = useFirebaseChatConfig();
  const {
    addConversation,
    findConversationSid,
    delayExecution,
    processing,
  } = useContext(ConverastionContext);
  const dispatch = useDispatch();
  const mutable = useMutableRef({
    client: useSelector(selectTwilioClient),
    disconnected: !useSelector(isTwilioConnected),
    allConversationsLoaded: useSelector(haveAllConversationsLoaded),
  });

  const displayConversation = useCallback((conversationSid: string, conversationType: ConversationType) => {
    batch(() => {
      dispatch(setChatInterface(true));
      dispatch(setCollapseState({ type: conversationType, value: true }));
      dispatch(setSelectedChat(conversationSid));
    });
  }, [dispatch]);

  // metaId should be something meaningful
  // e.g. in createPrivateChat, setting the secondParticipantUuid as metaId prevents duplicate loading components
  // Returns a callback to remove the loading component
  const createLoadingComponent = useCallback((conversationType: ConversationType, metaId: string) => {
    processing.add(metaId, conversationType);
    const loaderMetadata: ConversationMetadata = {
      id: metaId,
      conversationType,
      hasLoadedMessages: false,
      lastMessage: null,
      lastReadMessageIndex: -1,
      lastFetchedMessageIndex: null,
      dateCreated: new Date(),
      isLoading: true,
      uniqueName: null,
    };
    batch(() => {
      dispatch(setChatInterface(true));
      dispatch(setCollapseState({ type: conversationType, value: true }));
      dispatch(addMetadata(loaderMetadata));
    });
    return () => {
      processing.remove(metaId, conversationType);
      dispatch(removeMetadata(metaId));
    };
  }, [dispatch, processing]);

  const genericEnterChat = useCallback(async (
    chatIdentifier: string,
    type: ConversationType,
    conversationGenerator: () => Promise<Conversation>,
    onError?: (conversation: Conversation) => void,
  ) => {
    if (processing.checkExists(chatIdentifier, type)) return;

    let conversation: Conversation = null;
    const removeLoadingComponent = createLoadingComponent(type, chatIdentifier);
    try {
      conversation = await conversationGenerator();
      const initialMetadata = await buildInitialMetadata(conversation, uuid);

      addConversation(conversation, initialMetadata);
      batch(() => {
        removeLoadingComponent();
        dispatch(addMetadata(initialMetadata));
        displayConversation(conversation?.sid, type);
      });
    } catch (e) {
      if (onError && conversation) onError(conversation);
      batch(() => {
        removeLoadingComponent();
        dispatch(setChatInterface(true));
        dispatch(setChatAlert({
          message: e?.message ?? e,
          translationKey: e instanceof LocalizableError ? e.name : undefined,
          severity: 'error',
        }));
      });
    }
  }, [addConversation, createLoadingComponent, dispatch, displayConversation, processing, uuid]);

  const createPrivateChat = useCallback(async (secondParticipantUuid: string) => {
    if (!mutable.current.client || mutable.current.disconnected || uuid === secondParticipantUuid) return;
    if (!mutable.current.allConversationsLoaded) {
      delayExecution(() => createPrivateChat(secondParticipantUuid));
      return;
    }

    const conversationSid = findConversationSid(secondParticipantUuid, ConversationType.PRIVATE);
    if (conversationSid) {
      displayConversation(conversationSid, ConversationType.PRIVATE);
      return;
    }
    await genericEnterChat(
      secondParticipantUuid,
      ConversationType.PRIVATE,
      async () => {
        const conversationOptions = {
          attributes: {
            type: ConversationType.PRIVATE,
            domain: APP_DOMAIN,
          },
        } as ConversationsClient.CreateConversationOptions;
        const conversation = await mutable.current.client.createConversation(conversationOptions);
        await conversation.add(uuid);
        await conversation.add(secondParticipantUuid);
        return conversation;
      },
      (conversation) => conversation?.delete(),
    );
  }, [displayConversation, delayExecution, findConversationSid, genericEnterChat, mutable, uuid]);

  const joinGroupChat = useCallback(async (groupChatId: string, config: JoinGroupChatConfig = { groupChatType: GroupChatType.Booth, displayInterface: true }) => {
    if (!mutable.current.client || mutable.current.disconnected) return;
    if (config.groupChatType === GroupChatType.Booth && !exhibitorInteraction && isExhibitor && exhibitorBoothId !== groupChatId) {
      batch(() => {
        dispatch(setChatInterface(true));
        dispatch(setChatAlert({
          message: TRANSLATIONS.ERRORS.NO_EXHIBITOR_INTERACTION[1],
          translationKey: TRANSLATIONS.ERRORS.NO_EXHIBITOR_INTERACTION[0],
          severity: 'error',
        }));
      });
      return;
    }
    if (!mutable.current.allConversationsLoaded) {
      delayExecution(() => joinGroupChat(groupChatId));
      return;
    }

    const conversationSid = findConversationSid(groupChatId, ConversationType.GROUP);
    if (conversationSid) {
      if (config.displayInterface) displayConversation(conversationSid, ConversationType.GROUP);
      return;
    }
    await genericEnterChat(
      groupChatId,
      ConversationType.GROUP,
      async () => {
        const success = await joinConversationRequest(uuid, groupChatId);
        if (!success) {
          throw new LocalizableError({
            message: TRANSLATIONS.ERRORS.JOIN_FAIL[1],
            transKey: TRANSLATIONS.ERRORS.JOIN_FAIL[0],
          });
        }
        const groupConversation = await mutable.current.client.getConversationByUniqueName(groupChatId);
        await groupConversation.setAllMessagesRead();
        return groupConversation;
      },
    );
  }, [
    delayExecution,
    dispatch,
    displayConversation,
    exhibitorBoothId,
    exhibitorInteraction,
    findConversationSid,
    genericEnterChat,
    isExhibitor,
    mutable,
    uuid,
  ]);

  const joinPublicChatroom = useCallback(async (uniqueId: string) => {
    if (!mutable.current.client || mutable.current.disconnected) return;
    if (!mutable.current.allConversationsLoaded) {
      delayExecution(() => joinPublicChatroom(uniqueId));
      return;
    }

    const conversationSid = findConversationSid(uniqueId, ConversationType.PUBLIC);
    if (conversationSid) {
      displayConversation(conversationSid, ConversationType.PUBLIC);
      return;
    }
    await genericEnterChat(
      uniqueId,
      ConversationType.PUBLIC,
      async () => {
        const success = await joinConversationRequest(uuid, uniqueId);
        if (!success) {
          throw new LocalizableError({
            message: TRANSLATIONS.ERRORS.JOIN_FAIL[1],
            transKey: TRANSLATIONS.ERRORS.JOIN_FAIL[0],
          });
        }
        const publicConversation = await mutable.current.client.getConversationByUniqueName(uniqueId);
        await publicConversation.setAllMessagesRead();
        return publicConversation;
      },
    );
  }, [delayExecution, displayConversation, findConversationSid, genericEnterChat, mutable, uuid]);

  return useMemo(() => ({
    createPrivateChat,
    joinGroupChat,
    joinPublicChatroom,
  }), [
    createPrivateChat,
    joinGroupChat,
    joinPublicChatroom,
  ]);
};

export default useTwilioEventFunctions;
